import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faArrowLeft, faSave, faList, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { createProvider, updateProvider, fetchProvider } from '../../../../store/slices/providerSlice';
import { fetchPractice, addProviderOptimistic, fetchPracticePatients } from '../../../../store/slices/practiceSlice';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import { toast } from 'react-toastify';
import './AddProvider.css';
import api from '../../../../services/api';
import { validatePassword } from '../../../../utils/passwordValidation';
import AddFormModel from '../../../../components/Common/AddFormModel/AddFormModel';
import { buildPersonFormRows } from '../../../../components/Common/AddFormModel/personFormRows';
import FormActionButtons from '../../../../components/Common/FormActionButtons/FormActionButtons';
import FormDetailSkeleton from '../../../../components/SkeletonLoader/FormDetailSkeleton';
import { TIME_ZONES, TITLES, US_STATES, normalizeTimeZone } from '../../../../utils/constants';
import ReassignmentModal from '../../../../components/Common/ReassignmentModal/ReassignmentModal.jsx';

const AddProvider = () => {
  const navigate = useNavigate();
  const { practiceId } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { currentProvider, isLoading, isUpdating, isCreating } = useSelector(state => state.provider);
  const { currentPractice } = useSelector(state => state.practice ?? {});
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const editId = searchParams.get('editId');
  const isEditMode = Boolean(editId);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showReassignmentModal, setShowReassignmentModal] = useState(false);
  const [assignedPatients, setAssignedPatients] = useState([]);
  const [alternativeProviders, setAlternativeProviders] = useState([]);
  const [reassignments, setReassignments] = useState({}); // { patientId: newProviderId }
  const [isReassigning, setIsReassigning] = useState(false);
  const [isCheckingPasswordReuse, setIsCheckingPasswordReuse] = useState(false);
  const passwordReuseCheckTimerRef = React.useRef(null);
  const passwordReuseRequestIdRef = React.useRef(0);
  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    middleInitial: '',
    lastName: '',
    username: '',
    password: '',
    confirmPassword: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '', // Full Name
    stateId: null,
    zipCode: '',
    homePhone: '',
    cellPhone: '',
    fax: '',
    email: '',
    twoWayAuth: 'Disabled',
    timeZone: '',
    status: 'Active'
  });

  const [initialFormData, setInitialFormData] = useState(null);

  const isChanged = React.useMemo(() => {
    if (!isEditMode || !initialFormData) return true;
    if (formData.password?.trim() || formData.confirmPassword?.trim()) return true;
    return JSON.stringify({ ...formData, password: '', confirmPassword: '' }) !==
      JSON.stringify({ ...initialFormData, password: '', confirmPassword: '' });
  }, [formData, initialFormData, isEditMode]);

  const [errors, setErrors] = useState({});
  const hasPasswordReuseError = String(errors.password || '').toLowerCase().includes('different from current password');

  const getProviderSubmissionErrorMessage = (error, fallbackMessage) => {
    if (typeof error === 'string' && error.trim()) {
      return error;
    }

    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      fallbackMessage
    );
  };

  const applyServerFieldError = (message) => {
    if (!message) return false;

    const normalizedMessage = message.toLowerCase();
    const nextFieldErrors = {};

    if (normalizedMessage.includes('email') && (normalizedMessage.includes('exist') || normalizedMessage.includes('duplicate'))) {
      nextFieldErrors.email = message;
    }

    if (normalizedMessage.includes('username') && (normalizedMessage.includes('exist') || normalizedMessage.includes('duplicate') || normalizedMessage.includes('taken'))) {
      nextFieldErrors.username = message;
    }

    if (normalizedMessage.includes('password')) {
      nextFieldErrors.password = message;
    }

    if (Object.keys(nextFieldErrors).length === 0) {
      return false;
    }

    setErrors(prev => ({ ...prev, ...nextFieldErrors }));
    return true;
  };

  // Fetch provider data when in edit mode
  useEffect(() => {
    if (isEditMode && editId && practiceId) {
      dispatch(fetchProvider({ practiceId, providerId: editId }));
    }
  }, [isEditMode, editId, practiceId, dispatch]);

  // Fetch practice data if not already state
  useEffect(() => {
    if (practiceId && (!currentPractice || String(currentPractice.id) !== String(practiceId))) {
      dispatch(fetchPractice(practiceId));
    }
  }, [practiceId, currentPractice, dispatch]);

  useEffect(() => {
    return () => {
      clearBreadcrumbs();
    };
  }, [clearBreadcrumbs]);

  useEffect(() => {
    return () => {
      if (passwordReuseCheckTimerRef.current) {
        clearTimeout(passwordReuseCheckTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!practiceId) return;
    const practiceName = currentPractice?.practice_name || 'Practice';
    updateBreadcrumbs([
      { label: 'Practices', path: '/practices' },
      { label: practiceName, path: `/practices/${practiceId}?tab=practice` },
      { label: 'Providers', path: `/practices/${practiceId}?tab=provider` },
      { label: isEditMode ? 'Edit Provider' : 'Add Provider', path: null, active: true }
    ]);
  }, [practiceId, isEditMode, currentPractice, updateBreadcrumbs]);

  // Populate form when provider data is loaded
  useEffect(() => {
    if (isEditMode && currentProvider) {
      const stateId = currentProvider.address?.state_id || currentProvider.state_id;
      const state = US_STATES.find(s => s.id === stateId)?.name || '';

      const mappedData = {
        title: (currentProvider.title_id !== null && currentProvider.title_id !== undefined)
          ? String(currentProvider.title_id)
          : (TITLES.find(t => t.label === currentProvider.title)?.id ? String(TITLES.find(t => t.label === currentProvider.title).id) : ''),
        firstName: currentProvider.first_name || '',
        middleInitial: currentProvider.middle_name || '',
        lastName: currentProvider.last_name || '',
        username: currentProvider.username || '',
        password: '',
        confirmPassword: '',
        addressLine1: currentProvider.address?.address_line_1 || currentProvider.address_line_1 || '',
        addressLine2: currentProvider.address?.address_line_2 || currentProvider.address_line_2 || '',
        city: currentProvider.address?.city || currentProvider.city || '',
        state: state,
        stateId: stateId || null,
        zipCode: currentProvider.address?.zip_code || currentProvider.zip_code ? String(currentProvider.address?.zip_code || currentProvider.zip_code) : '',
        homePhone: formatPhoneNumber(currentProvider.phone || ''),
        cellPhone: formatPhoneNumber(currentProvider.cell_phone || ''),
        fax: formatPhoneNumber(currentProvider.fax || ''),
        email: currentProvider.email || '',
        twoWayAuth: currentProvider.is_two_way_auth === 1 || currentProvider.is_two_way_auth === true || currentProvider.two_way_auth === 'Enabled' ? 'Enabled' : 'Disabled',
        timeZone: normalizeTimeZone(currentProvider.time_zone) || '',
        status: currentProvider.status ? (currentProvider.status.charAt(0).toUpperCase() + currentProvider.status.slice(1).toLowerCase()) : 'Active'
      };
      setFormData(mappedData);
      setInitialFormData(mappedData);
    }
  }, [isEditMode, currentProvider]);

  // Helper for phone formatting (XXX) XXX - XXXX
  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)} - ${phoneNumber.slice(6, 10)}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newErrors = { ...errors };

    // Clear existing error for current field before re-validating
    delete newErrors[name];

    // 1. Phone Number Masking & Validation
    if (['homePhone', 'cellPhone', 'fax'].includes(name)) {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        const formattedValue = formatPhoneNumber(numericValue);
        setFormData(prev => ({ ...prev, [name]: formattedValue }));
        if (name === 'homePhone' && numericValue.length === 0) {
          newErrors.homePhone = 'Home Phone is required';
        } else if (numericValue.length > 0 && numericValue.length < 10 && name !== 'fax') {
          newErrors[name] = 'Invalid phone format';
        } else {
          delete newErrors[name];
        }
        setErrors(newErrors);
      }
      return;
    }

    // 1.1 Zip Code Validation (Prevention)
    if (name === 'zipCode') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 5) {
        setFormData(prev => ({ ...prev, [name]: numericValue }));
        if (numericValue.length > 0 && numericValue.length < 2) {
          newErrors.zipCode = 'Zip code must be at least 2 characters';
        } else {
          delete newErrors.zipCode;
        }
        setErrors(newErrors);
      }
      return;
    }

    // 1.2 State Sync
    if (name === 'state') {
      const selectedState = US_STATES.find(s => s.name === value);
      setFormData(prev => ({
        ...prev,
        state: value,
        stateId: selectedState ? selectedState.id : null
      }));
      return;
    }

    // 2. Strict Name Validation
    if (['firstName', 'lastName', 'middleInitial'].includes(name)) {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return; 
      }
      if ((name === 'firstName' || name === 'lastName') && value.length > 20) {
        return;
      }
      if (name === 'middleInitial') {
        if (value.includes(' ')) {
          newErrors.middleInitial = 'Middle Initial cannot contain spaces';
        } else {
          delete newErrors.middleInitial;
        }
      }
    }

    // 3. Password Validation (Real-time)
    if (name === 'password') {
      if (passwordReuseCheckTimerRef.current) {
        clearTimeout(passwordReuseCheckTimerRef.current);
        passwordReuseCheckTimerRef.current = null;
      }
      passwordReuseRequestIdRef.current += 1;
      setIsCheckingPasswordReuse(false);

      // Only perform real-time validation if the user has typed something.
      // If the field is cleared, simply remove the error message.
      if (!value) {
        setErrors(prev => {
          const { password, ...rest } = prev;
          return rest;
        });
      } else {
        const userData = {
          firstName: formData.firstName,
          lastName: formData.lastName
        };
        const validation = validatePassword(value, userData);
        if (!validation.isValid) {
          setErrors(prev => ({ ...prev, password: validation.error }));
        } else {
          setErrors(prev => {
            const { password, ...rest } = prev;
            return rest;
          });

          if (isEditMode && editId && value.trim()) {
            const requestId = passwordReuseRequestIdRef.current;
            setIsCheckingPasswordReuse(true);
            passwordReuseCheckTimerRef.current = setTimeout(async () => {
              try {
                await api.put(
                  `/practices/${practiceId}/providers/${editId}?validatePasswordOnly=1`,
                  { password: value }
                );
                if (requestId === passwordReuseRequestIdRef.current) {
                  setErrors(prev => {
                    const next = { ...prev };
                    if (String(next.password || '').toLowerCase().includes('different from current password')) {
                      delete next.password;
                    }
                    return next;
                  });
                }
              } catch (err) {
                const msg = err?.response?.data?.message || '';
                if (
                  requestId === passwordReuseRequestIdRef.current &&
                  String(msg).toLowerCase().includes('different from current password')
                ) {
                  setErrors(prev => ({ ...prev, password: msg }));
                }
              } finally {
                if (requestId === passwordReuseRequestIdRef.current) {
                  setIsCheckingPasswordReuse(false);
                }
              }
            }, 350);
          }
        }
      }
    }

    // 4. City Validation (Alpha only)
    if (name === 'city') {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
    }

    // 5. Status Change Detection
    if (name === 'status' && isEditMode) {
      const currentStatus = formData.status;
      if (currentStatus === 'Active' && value === 'Inactive') {
        startInactivationFlow();
        // Keep status unchanged until reassignment/no-patient flow completes.
        return;
      }
    }

    // Username validation (alphanumeric only)
    if (name === 'username') {
      const cleanedValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setFormData(prev => ({ ...prev, username: cleanedValue }));
      if (!cleanedValue) {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
      } else if (!/^[a-zA-Z0-9]+$/.test(cleanedValue)) {
        setErrors(prev => ({ ...prev, username: 'Username should only contain alphanumeric characters' }));
      } else {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
      }
      return;
    }

    // Email Validation (Real-time + Character Filtering)
    if (name === 'email') {
      const cleanedValue = value.replace(/\s/g, '');
      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
      
      setFormData(prev => ({ ...prev, email: cleanedValue }));
      
      if (!cleanedValue) {
        setErrors(prev => ({ ...prev, email: 'Email is required' }));
      } else if (cleanedValue && !emailRegex.test(cleanedValue)) {
        setErrors(prev => ({ ...prev, email: 'Invalid email format' }));
      } else {
        setErrors(prev => {
          const { email, ...rest } = prev;
          return rest;
        });
      }
      return; // Handled update
    }

    if (name === 'confirmPassword') {
      if (!value && !isEditMode) {
        newErrors.confirmPassword = 'Confirm Password is required';
      } else if (formData.password && value !== formData.password) {
        newErrors.confirmPassword = 'Passwords do not match';
      } else {
        delete newErrors.confirmPassword;
      }
    }

    const fieldLabels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      username: 'Username',
      addressLine1: 'Address Line 1',
      city: 'City',
      state: 'State',
      zipCode: 'Zip Code',
      timeZone: 'Time Zone',
      twoWayAuth: 'Two Way Authentication'
    };
    if (fieldLabels[name] && (!value || !String(value).trim())) {
      newErrors[name] = `${fieldLabels[name]} is required`;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name !== 'password' && name !== 'email') {
      setErrors(newErrors);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Helper function to safely trim string values
    const safeTrim = (value) => {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    };

    // Required fields validation
    if (!formData.firstName?.trim()) newErrors.firstName = 'First Name is required';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Last Name is required';
    if (!formData.username?.trim()) {
      newErrors.username = 'Username is required';
    } else if (!/^[a-zA-Z0-9]+$/.test(formData.username.trim())) {
      newErrors.username = 'Username should only contain alphanumeric characters';
    }
    if (!formData.timeZone) newErrors.timeZone = 'Time Zone is required';

    // Password Validation
    if (!isEditMode) {
      if (!formData.password?.trim()) {
        newErrors.password = 'Password is required';
      } else {
        const userData = { firstName: formData.firstName, lastName: formData.lastName };
        const validation = validatePassword(formData.password, userData);
        if (!validation.isValid) newErrors.password = validation.error;
      }
    } else if (formData.password?.trim()) {
      const userData = { firstName: formData.firstName, lastName: formData.lastName };
      const validation = validatePassword(formData.password, userData);
      if (!validation.isValid) newErrors.password = validation.error;
    }

    if (!isEditMode && !formData.confirmPassword?.trim()) newErrors.confirmPassword = 'Confirm Password is required';
    if (!formData.addressLine1?.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
    
    // City alpha validation
    if (!formData.city?.trim()) {
      newErrors.city = 'City is required';
    } else if (!/^[a-zA-Z\s]*$/.test(formData.city)) {
      newErrors.city = 'City can only contain alphabets';
    }

    if (!formData.state?.trim()) newErrors.state = 'State is required';
    
    // Zip Code
    if (!formData.zipCode?.trim()) {
      newErrors.zipCode = 'Zip Code is required';
    } else if (formData.zipCode.length < 2) {
      newErrors.zipCode = 'Zip code must be at least 2 characters';
    }

    // Email validation (trim extra spaces)
    const emailValue = (formData.email || '').trim();
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailValue) {
      newErrors.email = 'Email is required';
    } else if (/\s/.test(formData.email)) {
      newErrors.email = 'Email cannot contain spaces';
    } else if (!emailRegex.test(emailValue)) {
      newErrors.email = 'Invalid email format';
    }

    // Phone validation (fully filled)
    const numericHome = (formData.homePhone || '').replace(/\D/g, '');
    if (!numericHome) {
      newErrors.homePhone = 'Home Phone is required';
    } else if (numericHome.length < 10) {
      newErrors.homePhone = 'Home Phone must be exactly 10 digits';
    }

    const numericCell = (formData.cellPhone || '').replace(/\D/g, '');
    if (numericCell && numericCell.length < 10) {
      newErrors.cellPhone = 'Cell Phone must be exactly 10 digits';
    }

    const numericFax = (formData.fax || '').replace(/\D/g, '');
    if (numericFax && numericFax.length < 10) {
      newErrors.fax = 'Fax must be exactly 10 digits';
    }

    if (!formData.twoWayAuth?.trim()) newErrors.twoWayAuth = 'Two Way Authentication is required';
    if (!formData.timeZone?.trim()) newErrors.timeZone = 'Time Zone is required';
    if (!formData.status?.trim()) newErrors.status = 'Status is required';

    // Password confirmation
    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Debug mount
  useEffect(() => {
  }, []);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsSubmitting(true);

    if (validateForm()) {
      try {
        await saveProvider();
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(false);
      setShowValidationModal(true);
    }
  };
  const saveProvider = async () => {
    const providerData = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      middle_name: formData.middleInitial,
      email: (formData.email || '').trim(),
      phone: formData.homePhone,
      cell_phone: formData.cellPhone,
      fax: formData.fax,
      address_line_1: formData.addressLine1,
      address_line_2: formData.addressLine2,
      city: formData.city,
      zip_code: formData.zipCode,
      username: formData.username,
      status: isEditMode ? (formData.status || 'Active') : 'Active',
      is_two_way_auth: formData.twoWayAuth === 'Enabled' ? 1 : 0,
      time_zone: formData.timeZone,
      state_id: formData.stateId || 1,
      title_id: formData.title ? parseInt(formData.title) : null,
      password: formData.password || undefined
    };

    try {
      if (isEditMode) {
        await dispatch(updateProvider({ practiceId, providerId: editId, providerData })).unwrap();
        toast.success('Provider updated successfully');
      } else {
        const tempId = `temp-provider-${Date.now()}`;
        const optimisticProvider = {
          id: tempId,
          name: [formData.firstName, formData.lastName].filter(Boolean).join(' ').trim() || 'N/A',
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: (formData.email || '').trim(),
          home_phone: formData.homePhone || 'N/A',
          cell_phone: formData.cellPhone || 'N/A',
          fax: formData.fax || 'N/A',
          status: formData.status || 'Active',
          address: {
            address_line_1: formData.addressLine1 || 'N/A'
          }
        };

        dispatch(addProviderOptimistic({ practiceId, provider: optimisticProvider }));
        await dispatch(createProvider({ practiceId, providerData, tempId })).unwrap();
        toast.success('Provider added successfully');
      }
      navigate(`/practices/${practiceId}?tab=provider`);
    } catch (error) {
      console.error('Submission error:', error);
      const errorMessage = getProviderSubmissionErrorMessage(
        error,
        isEditMode ? 'Failed to update provider' : 'Failed to create provider'
      );
      applyServerFieldError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const startInactivationFlow = async () => {
    setIsSubmitting(true);
    try {
      // 1. Fetch patients for this provider
      const patientsResponse = await api.get(`/practices/${practiceId}/patients`, { 
        params: { providerId: editId, limit: 100 } 
      });
      const patients = patientsResponse.data?.data?.patients || [];
      setAssignedPatients(patients);

      // 2. Fetch alternative providers
      const providersResponse = await api.get(`/practices/${practiceId}/providers`, { params: { limit: 1000 } });
      const rawProviders =
        providersResponse.data?.data?.providers ||
        providersResponse.data?.providers ||
        providersResponse.data?.data ||
        providersResponse.data ||
        [];
      const providers = Array.isArray(rawProviders) ? rawProviders : [];
      
      // Filter out the current provider
      setAlternativeProviders(
        providers.filter((p) => {
          const status = p?.status;
          const isActive =
            status === 1 ||
            status === true ||
            (typeof status === 'string' && status.toLowerCase() === 'active');
          return String(p?.id) !== String(editId) && isActive;
        })
      );

      if (patients.length > 0) {
        // Initialize reassignments state
        const initialReassignments = {};
        patients.forEach(p => {
          initialReassignments[p.id] = '';
        });
        setReassignments(initialReassignments);
        setShowReassignmentModal(true);
      } else {
        // No patients, proceed with inactivation
        setFormData(prev => ({ ...prev, status: 'Inactive' }));
        toast.info('No patients assigned. You can now save the provider as Inactive.');
      }
    } catch (error) {
      console.error('Failed to start inactivation flow:', error);
      toast.error('Failed to check assigned patients.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBatchReassign = async () => {
    // Validate that all patients have a new provider selected
    const unassignedCount = Object.values(reassignments).filter(id => !id).length;
    if (unassignedCount > 0) {
      toast.warning(`Please select a new provider for all ${unassignedCount} patients.`);
      return;
    }

    setIsReassigning(true);
    try {
      const payload = {
        reassignments: Object.entries(reassignments).map(([patientId, providerId]) => ({
          patientId,
          providerId
        }))
      };

      await api.post(`/practices/${practiceId}/patients/batch-reassign-provider`, payload);
      
      setShowReassignmentModal(false);
      setFormData(prev => ({ ...prev, status: 'Inactive' }));
      // Refresh practice patient list immediately so patient table shows updated provider names without full reload.
      dispatch(fetchPracticePatients({ id: practiceId, params: { page: 1, limit: 1000 } }));
      toast.success('Patients reassigned successfully. Provider status set to Inactive.');
    } catch (error) {
      console.error('Batch reassignment failed:', error);
      toast.error('Failed to reassign patients.');
    } finally {
      setIsReassigning(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleCancel = () => {
    navigate(`/practices/${practiceId}?tab=provider`);
  };

  // Show loading state while fetching provider data in edit mode
  if (isEditMode && isLoading) {
    return (
      <div className="ap-container">
        <div className="add-patient-form-card" style={{ padding: '16px', display: 'grid', gap: '14px' }}>
          <FormDetailSkeleton titleLines={1} fieldRows={8} columns={2} showFooter={false} />
          <FormDetailSkeleton titleLines={1} fieldRows={2} columns={2} showFooter={true} />
        </div>
      </div>
    );
  }

  // Get logo URL - construct full URL if logo path exists
  const getLogoUrl = () => {
    if (!currentPractice?.practice_logo) return null;
    const logo = currentPractice.practice_logo;
    if (logo.startsWith('http://') || logo.startsWith('https://')) return logo;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    const baseUrl = API_URL.replace('/api', '');
    const logoPath = logo.startsWith('/') ? logo : `/${logo}`;
    return `${baseUrl}${logoPath}`;
  };

  return (
    <>
      <AddFormModel
        title={isEditMode ? 'Edit Provider' : 'Add Provider'}
        currentPractice={currentPractice}
        getLogoUrl={getLogoUrl}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        rows={buildPersonFormRows({
          formData,
          errors,
          isEditMode,
          handleInputChange,
          showPassword,
          showConfirmPassword,
          togglePasswordVisibility,
          toggleConfirmPasswordVisibility,
          phoneFieldName: 'homePhone',
          phoneLabel: 'Home Phone',
          phoneRequired: true,
        })}
        footerActions={
          <>
            {isEditMode ? (
              <button
                type="button"
                className="btn-submit"
                onClick={() => navigate(`/event-logs/Provider/${editId}?practiceId=${practiceId || ''}`)}
                title="View Change History"
                style={{ width: 'auto', paddingLeft: '15px', paddingRight: '15px', backgroundColor: '#19242E', marginRight: 'auto', height: '40px' }}
              >
                <FontAwesomeIcon icon={faList} className="me-2" /> Events Log
              </button>
            ) : (
              <div style={{ marginRight: 'auto' }}></div>
            )}
            <FormActionButtons
              onCancel={handleCancel}
              disabled={
                isSubmitting ||
                isLoading ||
                isUpdating ||
                isCreating ||
                isCheckingPasswordReuse ||
                (isEditMode && !isChanged) ||
                Boolean(errors.password) ||
                Boolean(errors.confirmPassword) ||
                hasPasswordReuseError
              }
              isSubmitting={isSubmitting || isUpdating || isCreating}
              isEditMode={isEditMode}
            />
          </>
        }
      />

      {/* Validation Warning Modal */}
      <Modal 
        show={showValidationModal} 
        onHide={() => setShowValidationModal(false)} 
        centered 
        className="add-provider-validation-modal"
      >
        <Modal.Body className="add-provider-modal-body" style={{ padding: '30px', textAlign: 'center' }}>
          <div className="add-provider-modal-icon-wrapper" style={{ marginBottom: '20px' }}>
            <FontAwesomeIcon icon={faInfoCircle} className="add-provider-modal-icon" style={{ fontSize: '40px', color: '#1A252F' }} />
          </div>
          <h4 className="add-provider-modal-title" style={{ color: '#1A252F', marginBottom: '20px', fontWeight: '600' }}>Fill all required fields</h4>
          <button 
            type="button"
            className="add-provider-modal-btn" 
            onClick={() => setShowValidationModal(false)}
            style={{ backgroundColor: '#1A252F', color: '#fff', border: 'none', padding: '10px 30px', borderRadius: '4px', cursor: 'pointer', width: '100%' }}
          >
            OK
          </button>
        </Modal.Body>
      </Modal>

      <ReassignmentModal
        show={showReassignmentModal}
        onClose={() => {
          setShowReassignmentModal(false);
          setFormData((prev) => ({ ...prev, status: 'Active' }));
        }}
        title="Reassign Patients"
        description={`Select a new provider for each of the ${assignedPatients.length} patient(s) before inactivation.`}
        patients={assignedPatients}
        alternatives={alternativeProviders}
        reassignments={reassignments}
        onReassignmentChange={(patientId, providerId) =>
          setReassignments((prev) => ({ ...prev, [patientId]: providerId }))
        }
        onSave={handleBatchReassign}
        isSaving={isReassigning}
        saveLabel="Save & Inactivate"
        optionPlaceholder="Select provider..."
        currentLabel="Current caregiver/provider"
        reassignToLabel="Reassign to provider"
        getCurrentAssignee={(patient) =>
          patient.provider_name ||
          patient.caregiver_name ||
          patient.assigned_to_name ||
          patient.current_provider_name ||
          ((patient.provider_first_name || patient.provider_last_name)
            ? `${patient.provider_first_name || ''} ${patient.provider_last_name || ''}`.trim()
            : '') ||
          ((patient.caregiver_first_name || patient.caregiver_last_name)
            ? `${patient.caregiver_first_name || ''} ${patient.caregiver_last_name || ''}`.trim()
            : '') ||
          'N/A'
        }
      />
    </>
  );
};
export default AddProvider;
